import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Loader from "../components/Loader";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const apiKey = process.env.REACT_APP_API_KEY;

// ===============================
// SPORT FIELD DEFINITIONS
// ===============================
const sportFields = {
  Basketball: {
    basic: [
      ["position", "Position (G/F/C/GF/FC)"],
      ["dominant_hand", "Dominant Hand (L/R/Both)"],
      ["height_in", "Height (in)"],
      ["wingspan_in", "Wingspan (in)"],
      ["vertical_jump_in", "Vertical Jump (in)"],
      ["forty_yard_time_sec", "40-Yard Time (sec)"],
    ],
    official: [
      ["gp", "Games Played (GP)"],
      ["min", "Minutes (MIN)"],
      ["pts", "Points (PTS)"],
      ["reb_o", "Offensive Rebounds"],
      ["reb_d", "Defensive Rebounds"],
      ["reb", "Total Rebounds"],
      ["ast", "Assists"],
      ["stl", "Steals"],
      ["blk", "Blocks"],
      ["tov", "Turnovers"],
      ["pf", "Personal Fouls"],
      ["fgm", "Field Goals Made"],
      ["fga", "Field Goals Attempted"],
      ["tpm", "3PT Made"],
      ["tpa", "3PT Attempted"],
      ["ftm", "Free Throws Made"],
      ["fta", "Free Throws Attempted"],
    ],
    derived: [
      ["fg_pct", "FG% = fgm/fga"],
      ["tp_pct", "3P% = tpm/tpa"],
      ["ft_pct", "FT% = ftm/fta"],
      ["efficiency", "Efficiency"],
    ],
  },
  Baseball: {
    basic: [
      ["bats", "Bats (R/L/S)"],
      ["throws", "Throws (R/L)"],
      ["positions", "Positions (P,C,1B,2B,3B,SS,LF,CF,RF)"],
      ["exit_velo_max_mph", "Exit Velo Max (mph)"],
      ["pop_time_sec", "Pop Time (sec)"],
      ["sixty_yard_time_sec", "60-Yard Time (sec)"],
    ],
    official: [
      // Hitting
      ["gp", "Games Played"],
      ["ab", "At Bats"],
      ["h", "Hits"],
      ["double", "Doubles"],
      ["triple", "Triples"],
      ["hr", "Home Runs"],
      ["bb", "Walks"],
      ["strikeouts", "Strikeouts"],
      ["r", "Runs"],
      ["rbi", "RBI"],
      ["sb", "Stolen Bases"],
      ["caught_stealing", "Caught Stealing"],
      ["hbp", "Hit By Pitch"],
      ["sf", "Sacrifice Flies"],
      // Pitching
      ["gs", "Games Started"],
      ["ip", "Innings Pitched"],
      ["bf", "Batters Faced"],
      ["er", "Earned Runs"],
      ["wp", "Wild Pitches"],
      ["bk", "Balks"],
      // Fielding
      ["po", "Putouts"],
      ["a", "Assists"],
      ["e", "Errors"],
    ],
    derived: [
      ["avg", "AVG = h/ab"],
      ["obp", "OBP = (h+bb+hbp)/(ab+bb+hbp+sf)"],
      ["slg", "SLG = TB/ab"],
      ["era", "ERA = (er*9)/ip"],
      ["whip", "WHIP = (bb+h)/ip"],
      ["fpct", "FPCT = (po+a)/(po+a+e)"],
    ],
  },
  Football: {
    basic: [
      ["handedness", "Handedness (L/R/Both)"],
      ["forty_yard_time_sec", "40-Yard Time (sec)"],
      ["bench_reps_225", "Bench Reps (225 lbs)"],
      ["vertical_jump_in", "Vertical Jump (in)"],
      ["positions", "Positions (QB,RB,WR,TE,OL,DL,LB,CB,S,K,P,KR,PR)"],
    ],
    official: [
      // QB
      ["cmp", "Completions"],
      ["att", "Attempts"],
      ["yds", "Passing Yards"],
      ["td", "Passing TD"],
      ["int", "Interceptions"],
      ["sacks", "Sacks Taken"],
      ["rush_att", "Rush Attempts"],
      ["rush_yds", "Rush Yards"],
      ["rush_td", "Rush TD"],
      // RB
      ["rec", "Receptions"],
      ["rec_yds", "Receiving Yards"],
      ["rec_td", "Receiving TD"],
      // WR/TE
      ["tgt", "Targets"],
      // Defense
      ["tkl", "Tackles"],
      ["tfl", "Tackles For Loss"],
      ["sack", "Sacks"],
      ["pd", "Passes Defended"],
      ["ff", "Forced Fumbles"],
      ["fr", "Fumble Recoveries"],
      // Kicking
      ["fg_m", "FG Made"],
      ["fg_a", "FG Attempts"],
      ["xp_m", "XP Made"],
      ["xp_a", "XP Attempts"],
      ["long_fg", "Longest FG"],
      // Returns
      ["kr", "Kick Returns"],
      ["kr_yds", "Kick Return Yards"],
      ["kr_td", "Kick Return TD"],
      ["pr", "Punt Returns"],
      ["pr_yds", "Punt Return Yards"],
      ["pr_td", "Punt Return TD"],
    ],
    derived: [
      ["y_a", "Yards/Attempt = yds/att"],
      ["fg_pct", "FG% = fg_m/fg_a"],
      ["xp_pct", "XP% = xp_m/xp_a"],
    ],
  },
  Hockey: {
    basic: [
      ["shoots", "Shoots (L/R)"],
      ["position", "Position (F/D/G)"],
    ],
    official: [
      // Skater
      ["gp", "Games Played"],
      ["g", "Goals"],
      ["hockey_a", "Assists"],
      ["pts", "Points"],
      ["plus_minus", "+/-"],
      ["pim", "Penalty Minutes"],
      ["sog", "Shots on Goal"],
      ["hits", "Hits"],
      ["blk_s", "Blocks"],
      ["toi_min", "Time on Ice (min)"],
      ["fo_w", "Faceoff Wins"],
      ["fo_l", "Faceoff Losses"],
      // Goalie
      ["gs", "Games Started"],
      ["min", "Minutes Played"],
      ["sa", "Shots Against"],
      ["hockey_saves", "Saves"],
      ["hockey_goals_allowed", "Goals Against"],
      ["hockey_shutouts", "Shutouts"],
    ],
    derived: [
      ["fo_pct", "FO% = fo_w/(fo_w+fo_l)"],
      ["sv_pct", "Save% = sv/sa"],
      ["gaa", "GAA = (ga*60)/min"],
    ],
  },
  Soccer: {
    basic: [
      ["dominant_foot", "Dominant Foot (L/R/Both)"],
      ["positions", "Positions (GK/DF/MF/FW)"],
      ["forty_meter_time_sec", "40-Meter Time (sec)"],
    ],
    official: [
      // Field
      ["gp", "Games Played"],
      ["min", "Minutes Played"],
      ["g", "Goals"],
      ["a", "Assists"],
      ["shots", "Shots"],
      ["sot", "Shots on Target"],
      ["chances_created", "Chances Created"],
      ["passes", "Passes"],
      ["tackles", "Tackles"],
      ["interceptions", "Interceptions"],
      ["clearances", "Clearances"],
      ["fouls_committed", "Fouls Committed"],
      ["fouls_suffered", "Fouls Suffered"],
      ["yc", "Yellow Cards"],
      ["rc", "Red Cards"],
      // Goalkeeper
      ["soccer_saves", "Saves"],
      ["g_a", "Goals Against"],
      ["soccer_clean_sheets", "Clean Sheets"],
    ],
    derived: [
      ["shot_acc", "Shot Accuracy = sot/shots"],
      ["sv_pct", "Save% = soccer_saves/(soccer_saves+g_a)"],
    ],
  },
};

// ===============================
// COMPONENT
// ===============================
export default function ShowStats() {
  const { id } = useParams();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const API_URL = `${BASE_URL}/getplayers`;
  const SAVE_URL = `${BASE_URL}/addplayer`;

  useEffect(() => {
    axios
    .get(API_URL, {
      headers: { Authorization: apiKey, "Content-Type": "application/json" },
    })
    .then((res) => {
      let foundPlayer = res.data.find((p) => p.Id === id);

      if (foundPlayer && foundPlayer.stats) {
        const official = foundPlayer.stats.official_stats || {};
        const derived = foundPlayer.stats.derived || {};

        // ✅ only flatten if nested
        const flatOfficial =
          official && Object.values(official)[0] instanceof Object
            ? Object.values(official).reduce((acc, obj) => ({ ...acc, ...obj }), {})
            : official;

        const flatDerived =
          derived && Object.values(derived)[0] instanceof Object
            ? Object.values(derived).reduce((acc, obj) => ({ ...acc, ...obj }), {})
            : derived;

        foundPlayer = {
          ...foundPlayer,
          stats: {
            ...foundPlayer.stats,
            official_stats: flatOfficial,
            derived: flatDerived,
          },
        };
      }

      setPlayer(foundPlayer || null);
      setLoading(false);
    })
    .catch(() => {
      toast.error("Failed to load player data.");
      setLoading(false);
    });
  }, [API_URL, id]);

  if (loading) return <Loader />;
  if (!player) return <p className="text-center mt-4">Player not found.</p>;

  const stats = player.stats || {};
  const sport = sportFields[player.SportCategory] || null;

  const handleChange = (section, field, value) => {
    setPlayer((prev) => ({
      ...prev,
      stats: {
        ...prev.stats,
        [section]: {
          ...prev.stats?.[section],
          [field]: value,
        },
      },
    }));
  };

  const computeDerivedStats = (sport, official) => {
    const derived = {};

    switch (sport) {
      case "Basketball":
        derived.fg_pct = official.fga ? (official.fgm / official.fga).toFixed(3) : 0;
        derived.tp_pct = official.tpa ? (official.tpm / official.tpa).toFixed(3) : 0;
        derived.ft_pct = official.fta ? (official.ftm / official.fta).toFixed(3) : 0;
        derived.efficiency = (
          (Number(official.pts || 0) +
            Number(official.reb || 0) +
            Number(official.ast || 0) +
            Number(official.stl || 0) +
            Number(official.blk || 0) -
            (Number(official.fga || 0) -
              Number(official.fgm || 0) +
              Number(official.fta || 0) -
              Number(official.ftm || 0) +
              Number(official.tov || 0)))
        ).toFixed(2);
        break;

      case "Baseball":
        derived.avg = official.ab ? (official.h / official.ab).toFixed(3) : 0;
        derived.obp =
          official.ab + official.bb + official.hbp + official.sf
            ? ((Number(official.h || 0) + Number(official.bb || 0) + Number(official.hbp || 0)) /
                (Number(official.ab || 0) +
                  Number(official.bb || 0) +
                  Number(official.hbp || 0) +
                  Number(official.sf || 0))).toFixed(3)
            : 0;
        derived.slg = official.ab ? ( // SLG needs total bases
          (Number(official.h || 0) +
            Number(official.double || 0) +
            2 * Number(official.triple || 0) +
            3 * Number(official.hr || 0)) /
          Number(official.ab || 0)
        ).toFixed(3) : 0;
        derived.era = official.ip ? ((official.er * 9) / official.ip).toFixed(2) : 0;
        derived.whip = official.ip ? ((Number(official.bb || 0) + Number(official.h || 0)) / official.ip).toFixed(3) : 0;
        derived.fpct =
          Number(official.po || 0) + Number(official.a || 0) + Number(official.e || 0)
            ? ((Number(official.po || 0) + Number(official.a || 0)) /
                (Number(official.po || 0) + Number(official.a || 0) + Number(official.e || 0))).toFixed(3)
            : 0;
        break;

      case "Football":
        derived.y_a = official.att ? (official.yds / official.att).toFixed(2) : 0;
        derived.fg_pct = official.fg_a ? (official.fg_m / official.fg_a).toFixed(3) : 0;
        derived.xp_pct = official.xp_a ? (official.xp_m / official.xp_a).toFixed(3) : 0;
        break;

      case "Hockey":
        derived.fo_pct =
          (Number(official.fo_w || 0) + Number(official.fo_l || 0))
            ? (official.fo_w / (Number(official.fo_w || 0) + Number(official.fo_l || 0))).toFixed(3)
            : 0;
        derived.sv_pct = official.sa ? (official.sv / official.sa).toFixed(3) : 0;
        derived.gaa = official.min ? ((official.ga * 60) / official.min).toFixed(2) : 0;
        break;

      case "Soccer":
        derived.shot_acc = official.shots ? (official.sot / official.shots).toFixed(3) : 0;
        derived.sv_pct =
          (Number(official.soccer_saves || 0) + Number(official.g_a || 0))
            ? (official.soccer_saves / (Number(official.soccer_saves || 0) + Number(official.g_a || 0))).toFixed(3)
            : 0;
        break;

      default:
        break;
    }

    return derived;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let basicInputs = { ...player.stats?.basic_inputs };
      let officialStats = { ...player.stats?.official_stats };

      // ✅ recompute derived before saving
      let derived = computeDerivedStats(player.SportCategory, officialStats);

      if (basicInputs.positions && typeof basicInputs.positions === "string") {
        basicInputs.positions = basicInputs.positions
          .split(",")
          .map((p) => p.trim())
          .filter((p) => p.length > 0);
      }

      // ✅ Ensure all Hockey fields are always included
      let hockeyOfficial = {};
      if (player.SportCategory === "Hockey") {
        sportFields.Hockey.official.forEach(([key]) => {
          hockeyOfficial[key] = officialStats[key] ?? "";
        });
        sportFields.Hockey.derived.forEach(([key]) => {
          hockeyOfficial[key] = derived[key] ?? "";
        });
        sportFields.Hockey.basic.forEach(([key]) => {
          hockeyOfficial[key] = basicInputs[key] ?? "";
        });
      }

      const payload = {
        Id: player.Id,
        SportCategory: player.SportCategory,
        PlayerName: player.PlayerName,
        EventName: player.EventName,
        EventDate: player.EventDate,
        CityLocation: player.CityLocation,
        Email: player.Email,
        JerseyNumber: player.JerseyNumber,
        Status: player.Status,
        ...basicInputs,
        ...officialStats,
        ...derived,
        ...(player.SportCategory === "Hockey" ? hockeyOfficial : {}),
      };

      console.log("Payload sent:", payload);

      const res = await axios.post(SAVE_URL, payload, {
        headers: { Authorization: apiKey, "Content-Type": "application/json" },
      });

      toast.success(res.data.message || "Stats saved successfully!");
    } catch (err) {
      console.error("Save error:", err.response?.data || err);
      toast.error("Failed to save stats.");
    } finally {
      setSaving(false);
    }
  };

  const renderTable = (title, fields, section, editable = true) => {
    const sectionData = stats?.[section] || {};
    return (
      <div className="mt-4 w-75 mx-auto">
        <h5 className="mb-2">{title}</h5>
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>Stat</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {fields.map(([key, label]) => (
              <tr key={key}>
                <td>{label}</td>
                <td>
                  {editable ? (
                    <input
                      type="text"
                      className="form-control"
                      value={sectionData[key] ?? ""}
                      onChange={(e) => handleChange(section, key, e.target.value)}
                    />
                  ) : (
                    sectionData[key] ?? "-"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="container mt-4">
      <h2 className="text-center mb-4">
        {player.PlayerName} – {player.SportCategory} Stat Sheet
      </h2>

      {/* Basic Info */}
      <table className="table table-bordered w-75 mx-auto">
        <tbody>
          <tr>
            <td><strong>Player Name:</strong> {player.PlayerName}</td>
            <td><strong>Event Name:</strong> {player.EventName}</td>
          </tr>
          <tr>
            <td><strong>Event Date / Code:</strong> {player.EventDate} / {player.EventCode}</td>
            <td><strong>City/Location:</strong> {player.CityLocation}</td>
          </tr>
          <tr>
            <td><strong>Email:</strong> {player.Email}</td>
            <td><strong>Jersey Number:</strong> {player.JerseyNumber}</td>
          </tr>
          <tr>
            <td colSpan="2"><strong>Notes:</strong> {player.Notes}</td>
          </tr>
        </tbody>
      </table>

      {/* Media */}
      <div className="w-75 mx-auto mt-4">
        <div className="row">
          {player.VideoFile && (
            <div className="col-md-6 mb-4">
              <h5 className="text-center">📹 Player Video</h5>
              <video
                src={`${player.VideoFile}`}
                width="100%" height="400"
                controls
                style={{ border: "1px solid #ccc", borderRadius: "8px" }}
              />
            </div>
          )}
          {player.DocumentFile && (
            <div className="col-md-6 mb-4">
              <h5 className="text-center">📄 Player PDF</h5>
              <iframe
                src={`${player.DocumentFile}`}
                width="100%" height="400"
                style={{ border: "1px solid #ccc", borderRadius: "8px" }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Stats Sections */}
      {sport && (
        <>
          {renderTable("Athlete Basic Inputs (User/Community)", sport.basic, "basic_inputs")}
          {renderTable("Official Game Stats (Admin/Scorer)", sport.official, "official_stats")}
          {renderTable("Derived Stats (Server-Computed)", sport.derived, "derived", false)}
        </>
      )}

      {/* Save */}
      <div className="text-center mt-4">
        <button
          className="btn btn-success me-2"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Stats"}
        </button>
        <Link to="/players" className="btn btn-secondary">
          Back to Players
        </Link>
      </div>

      <ToastContainer position="top-center" />
    </div>
  );
}
